import { Injectable, Signal, computed, effect, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { DotCMSGraphQLParams, DotCMSPageAsset, DotCMSPageRequestParams } from '@dotcms/types';
import { getUVEState } from '@dotcms/uve';
import { concatLatestFrom } from '@ngrx/operators';
import { Store, select } from '@ngrx/store';
import { isEqual } from 'lodash-es';
import { Observable, combineLatest, debounceTime, distinctUntilChanged, map, shareReplay, take } from 'rxjs';

import { AppFacade } from 'ish-core/facades/app.facade';
import { DeviceType } from 'ish-core/models/viewtype/viewtype.types';
import {
  getCurrentDotCmsApiConfiguration,
  getCurrentDotCmsFooterIdentifier,
  getCurrentDotCmsPersona,
  getCurrentLocale,
  getDotCmsLanguageConfiguration,
} from 'ish-core/store/core/configuration';
import { selectCleanUrl, selectPreviousUrl } from 'ish-core/store/core/router/router.selectors';
import { whenTruthy } from 'ish-core/utils/operators';

import { ExtraContent, VideoContentlet } from '../models/dot-cms-uve-contentlet.model';
import { PageState } from '../models/dot-cms-uve.model';
import { DotCmsUveEditablePageService } from '../services/dot-cms-uve-editable-page/dot-cms-uve-editable-page.service';
import { BASE_EXTRA_QUERIES, createNavigationQuery, footerQuery, fragmentNav } from '../shared/queries/queries';

type DotCMSPage = {
  pageAsset: DotCMSPageAsset;
  content: ExtraContent;
};

interface Deps {
  personaId: string | undefined;
  languageId: number | undefined;
  footerIdentifier: string | undefined;
  url: string | undefined;
}

/* eslint-disable @typescript-eslint/member-ordering */
@Injectable({ providedIn: 'root' })
export class DotCmsUveFacade {
  private store = inject(Store);
  private appFacade = inject(AppFacade);
  private editablePageService = inject<DotCmsUveEditablePageService<DotCMSPage>>(DotCmsUveEditablePageService);

  // State and signals
  private readonly _pageState = signal<PageState<DotCMSPage> | null>(null);
  readonly pageState = computed(() => this._pageState());
  private initializedOnce = false;
  private lastDeps: Deps | null = null;
  private currentPageSignal = signal<Signal<PageState<DotCMSPage> | null> | null>(null);

  private readonly currentLocale$ = this.store.pipe(select(getCurrentLocale), whenTruthy(), take(1));

  private readonly languageId$ = this.currentLocale$.pipe(
    concatLatestFrom(() => this.store.pipe(select(getDotCmsLanguageConfiguration))),
    map(([locale, config]) => config[locale])
  );

  private readonly deps$ = combineLatest([
    this.store.pipe(select(getCurrentDotCmsPersona), distinctUntilChanged()),
    this.languageId$.pipe(distinctUntilChanged()),
    this.store.pipe(select(getCurrentDotCmsFooterIdentifier), distinctUntilChanged()),
    this.store.pipe(select(selectCleanUrl), distinctUntilChanged()),
  ]).pipe(
    debounceTime(200),
    distinctUntilChanged(isEqual),
    shareReplay(1),
    map(([personaId, languageId, footerIdentifier, url]) => ({
      personaId,
      languageId,
      footerIdentifier,
      url,
    }))
  );

  private readonly depsSignal = toSignal(this.deps$, { initialValue: null });

  constructor() {
    // Effect to handle dependency changes and create pageSignal
    effect(
      () => {
        const deps = this.depsSignal();
        if (!deps || (this.lastDeps && isEqual(this.lastDeps, deps))) {
          return;
        }

        if (!this.initializedOnce || deps.url?.includes('/page') || deps.url === '/home') {
          this.initializedOnce = true;
          this.lastDeps = deps;
          this.initializePageSignal(deps);
        }
      },
      { allowSignalWrites: true }
    );

    // Effect to update _pageState
    effect(
      () => {
        const pageSignal = this.currentPageSignal()?.();
        if (!pageSignal) {
          return;
        }

        try {
          this._pageState.set(pageSignal);
        } catch (error) {
          console.error('Failed to update page state:', error);
          this._pageState.set({ status: 'error', error: undefined });
        }
      },
      { allowSignalWrites: true }
    );
  }

  private initializePageSignal(deps: Deps): void {
    const graphqlQueries = this.buildGraphQLQueries(deps);
    const initConfig: DotCMSPageRequestParams = {
      graphql: graphqlQueries,
      ...(deps.personaId && { personaId: deps.personaId }),
      ...(deps.languageId && { languageId: deps.languageId }),
    };

    try {
      const rawPageSignal = this.editablePageService.initializePage(initConfig, deps.url!);
      let lastState: PageState<DotCMSPage> | null = null;
      const deduplicatedPageSignal = computed(() => {
        const state = rawPageSignal();
        if (!state || state.status === 'loading') {
          return null;
        }
        if (lastState && isEqual(this.normalizeState(lastState), this.normalizeState(state))) {
          return null;
        }
        lastState = state;
        return this.mergePageState(state);
      });

      this.currentPageSignal.set(deduplicatedPageSignal);
    } catch (error) {
      console.error('Failed to initialize page signal:', error);
      this._pageState.set({ status: 'error', error: undefined });
    }
  }

  private buildGraphQLQueries(deps: Deps): DotCMSGraphQLParams {
    const currentPageState = this._pageState();
    //@ts-ignore
    const hasFooter = currentPageState?.pageResponse?.content?.footer !== undefined;
    const hasHeaderNavigation = currentPageState?.pageResponse?.content?.navigation !== undefined;
    const isEditMode = getUVEState()?.mode === 'EDIT_MODE';
    const graphqlQueries: DotCMSGraphQLParams = { ...BASE_EXTRA_QUERIES };

    if (deps.footerIdentifier && (isEditMode || !hasFooter)) {
      graphqlQueries.content = {
        ...graphqlQueries.content,
        footer: footerQuery(deps.footerIdentifier),
      };
    }

    if (!hasHeaderNavigation) {
      graphqlQueries.content = {
        ...graphqlQueries.content,
        navigation: createNavigationQuery(5),
      };
      graphqlQueries.fragments = [...(graphqlQueries.fragments || []), fragmentNav];
    }

    return graphqlQueries;
  }

  private mergePageState(newState: PageState<DotCMSPage>): PageState<DotCMSPage> {
    const currentPageState = this._pageState();
    return {
      ...newState,
      pageResponse: {
        ...newState.pageResponse,
        //@ts-ignore
        content: {
          ...(currentPageState?.pageResponse?.content || {}),
          //@ts-ignore
          ...(newState.pageResponse?.content?.footer !== undefined
            ? //@ts-ignore
              { footer: newState.pageResponse.content.footer }
            : {}),
          ...(newState.pageResponse?.content?.navigation !== undefined
            ? { navigation: newState.pageResponse.content.navigation }
            : {}),
        },
      },
    };
  }

  private normalizeState(state: PageState<DotCMSPage> | null) {
    if (!state) {
      return null;
    }
    return {
      ...state,
      pageResponse: state.pageResponse
        ? {
            ...state.pageResponse,
            pageAsset: state.pageResponse.pageAsset
              ? {
                  ...state.pageResponse.pageAsset,
                  page: state.pageResponse.pageAsset.page ? { ...state.pageResponse.pageAsset.page } : undefined,
                }
              : undefined,
            graphql: state.pageResponse.graphql ? { ...state.pageResponse.graphql } : undefined,
          }
        : undefined,
    };
  }

  getDotCmsHost$ = this.store.pipe(select(getCurrentDotCmsApiConfiguration)).pipe(map(config => config.host));

  getVideoHeight(contentlet: VideoContentlet): Observable<string> {
    return this.appFacade.deviceType$.pipe(
      map(deviceType => this.generateVideoHeightUsingDeviceType(contentlet, deviceType))
    );
  }

  private generateVideoHeightUsingDeviceType(contentlet: VideoContentlet, deviceType: DeviceType) {
    return deviceType === 'desktop'
      ? contentlet.heightlg
      : deviceType === 'tablet'
      ? contentlet.heightmd
      : deviceType === 'mobile'
      ? contentlet.heightsm
      : '';
  }

  getVideoClasses(contentlet: VideoContentlet): string {
    if (contentlet?.maxWidth) {
      return `${contentlet.cssClass}` + ` video-max-width`;
    }

    return contentlet.cssClass;
  }

  getPreviousUrl(): Observable<string> {
    return this.store.pipe(select(selectPreviousUrl));
  }

  currentPageUrl(): Observable<string> {
    return this.store.pipe(select(selectCleanUrl));
  }
}
